import React, { useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { projects } from "../data/mock";

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const project = useMemo(() => projects.find((p) => p.id === id), [id]);
  const idx = projects.findIndex((p) => p.id === id);
  const next = idx >= 0 ? projects[(idx + 1) % projects.length] : null;
  const prev = idx >= 0 ? projects[(idx - 1 + projects.length) % projects.length] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) {
    return (
      <div className="min-h-screen bg-[#f5f3ee] text-[#0d0d0d] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[11px] tracking-[0.18em] uppercase text-[#6b6b64] mb-3">
            404 / Project not found
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[14px] border-b border-[#0d0d0d] pb-0.5"
          >
            <ArrowLeft size={14} /> Back to portfolio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f5f3ee] text-[#0d0d0d] min-h-screen antialiased">
      {/* Slim top bar (no full header) */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[rgba(245,243,238,0.9)] backdrop-blur-md border-b border-[#d8d5cc]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link
            to="/#work"
            className="inline-flex items-center gap-2 text-[13px] text-[#0d0d0d] hover:opacity-60 transition-opacity duration-200"
          >
            <ArrowLeft size={16} />
            <span>Back to work</span>
          </Link>
          <Link to="/" className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-8 h-8 border border-[#0d0d0d] text-[#0d0d0d] font-medium tracking-tight text-[12px]">
              EM
            </span>
            <span className="hidden md:inline text-[13px] tracking-tight">
              Evan Magee
            </span>
          </Link>
          <div />
        </div>
      </header>

      {/* Hero / project meta */}
      <section className="px-6 md:px-10 max-w-[1400px] mx-auto pt-28 md:pt-36 pb-12 md:pb-20">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-3">
            <p className="text-[11px] tracking-[0.18em] uppercase text-[#6b6b64]">
              ({project.index}) {project.category}
            </p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h1 className="font-display text-[44px] md:text-[88px] leading-[0.98] tracking-[-0.025em] text-[#0d0d0d] max-w-5xl">
              {project.title}
            </h1>
            <p className="mt-6 italic font-serif text-[18px] md:text-[22px] text-[#3a3a36] max-w-3xl">
              {project.client} — {project.role}
            </p>
            {project.award && (
              <p className="mt-3 text-[11px] tracking-[0.18em] uppercase text-[#0d0d0d]">
                ★ {project.award}
              </p>
            )}
          </div>
        </div>

        {/* Meta strip */}
        <div className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 border-t border-[#0d0d0d]">
          {[
            { label: "Client", value: project.client },
            { label: "Year", value: project.year },
            { label: "Role", value: project.role },
            { label: "Discipline", value: project.category },
          ].map((m, i) => (
            <div
              key={m.label}
              className={`p-4 md:p-5 border-b border-[#d8d5cc] ${
                i !== 3 ? "md:border-r border-[#d8d5cc]" : ""
              }`}
            >
              <p className="text-[10px] tracking-[0.18em] uppercase text-[#6b6b64] mb-2">
                {m.label}
              </p>
              <p className="text-[14px] md:text-[15px] text-[#0d0d0d]">
                {m.value}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Description */}
      <section className="px-6 md:px-10 max-w-[1400px] mx-auto pb-12 md:pb-16">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-3">
            <p className="text-[11px] tracking-[0.18em] uppercase text-[#6b6b64]">
              Overview
            </p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <p className="text-[18px] md:text-[22px] leading-[1.5] text-[#0d0d0d] max-w-3xl">
              {project.description}
            </p>
            {project.tags?.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2">
                {project.tags.map((t) => (
                  <span
                    key={t}
                    className="text-[12px] tracking-tight px-3 py-1.5 border border-[#d8d5cc] text-[#3a3a36]"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
            {project.externalLink && (
              <a
                href={project.externalLink.url}
                target="_blank"
                rel="noreferrer"
                className="mt-8 inline-flex items-center gap-2 text-[14px] tracking-tight bg-[#0d0d0d] text-[#f5f3ee] px-5 py-3 hover:bg-[#2a2a2a] transition-colors duration-200"
              >
                {project.externalLink.label}
                <ArrowUpRight size={16} />
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Pitch deck sub-grid (only when a project has a `decks` array) */}
      {project.decks?.length > 0 && (
        <section className="px-6 md:px-10 max-w-[1400px] mx-auto pb-16 md:pb-24">
          <div className="grid grid-cols-12 gap-4 mb-8">
            <div className="col-span-12 md:col-span-3">
              <p className="text-[11px] tracking-[0.18em] uppercase text-[#6b6b64]">
                The Decks
              </p>
            </div>
            <div className="col-span-12 md:col-span-9">
              <p className="italic font-serif text-[15px] md:text-[16px] text-[#3a3a36] max-w-2xl">
                Click any deck to open the full presentation.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-10">
            {project.decks.map((d, i) => (
              <a
                key={d.id}
                href={d.url}
                target="_blank"
                rel="noreferrer"
                className="group block"
              >
                <div className="relative overflow-hidden bg-[#0d0d0d] aspect-[16/9]">
                  <img
                    src={d.image}
                    alt={d.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-[#0d0d0d] opacity-0 group-hover:opacity-15 transition-opacity duration-500" />
                  <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#f5f3ee] text-[#0d0d0d] flex items-center justify-center opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                    <ArrowUpRight size={14} />
                  </div>
                  <div className="absolute top-3 left-3 text-[10px] tracking-[0.18em] uppercase text-[#f5f3ee] mix-blend-difference">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="mt-4 flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="font-display text-[17px] md:text-[18px] leading-[1.2] tracking-[-0.01em] text-[#0d0d0d]">
                      {d.title}
                    </h3>
                    {d.subtitle && (
                      <p className="mt-1 italic font-serif text-[13px] text-[#6b6b64]">
                        {d.subtitle}
                      </p>
                    )}
                  </div>
                  <span className="text-[10px] tracking-[0.18em] uppercase text-[#6b6b64] pt-1">
                    View →
                  </span>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Gallery (PDF page spreads) */}
      {project.gallery?.length > 0 && (
        <section className="px-6 md:px-10 max-w-[1400px] mx-auto pb-20 md:pb-28">
          <div className="grid grid-cols-12 gap-4 mb-8">
            <div className="col-span-12 md:col-span-3">
              <p className="text-[11px] tracking-[0.18em] uppercase text-[#6b6b64]">
                The Work
              </p>
            </div>
          </div>
          <div className="space-y-8 md:space-y-12">
            {project.gallery.map((g, i) => (
              <figure key={i} className="space-y-3">
                <div className="bg-[#0d0d0d] overflow-hidden">
                  <img
                    src={g.src}
                    alt={`${project.title} — ${g.caption}`}
                    loading={i === 0 ? "eager" : "lazy"}
                    className="w-full h-auto block"
                  />
                </div>
                {g.caption && (
                  <figcaption className="flex items-center justify-between text-[12px] text-[#6b6b64] tracking-tight">
                    <span className="italic font-serif">{g.caption}</span>
                    <span className="text-[10px] tracking-[0.18em] uppercase">
                      {String(i + 1).padStart(2, "0")} / {String(project.gallery.length).padStart(2, "0")}
                    </span>
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* Prev / next + CTA */}
      <section className="bg-[#0d0d0d] text-[#f5f3ee]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          {prev && (
            <button
              onClick={() => navigate(`/work/${prev.id}`)}
              className="text-left group border-t border-[#2a2a2a] pt-6 hover:border-[#f5f3ee] transition-colors duration-300"
            >
              <p className="text-[11px] tracking-[0.18em] uppercase text-[#8a8a85] mb-3">
                ← Previous
              </p>
              <p className="font-display text-[28px] md:text-[40px] leading-[1.05] tracking-[-0.02em] group-hover:opacity-80 transition-opacity">
                {prev.title}
              </p>
              <p className="mt-2 italic font-serif text-[14px] text-[#bdbdb5]">
                {prev.client}
              </p>
            </button>
          )}
          {next && (
            <button
              onClick={() => navigate(`/work/${next.id}`)}
              className="text-right group border-t border-[#2a2a2a] pt-6 hover:border-[#f5f3ee] transition-colors duration-300"
            >
              <p className="text-[11px] tracking-[0.18em] uppercase text-[#8a8a85] mb-3 inline-flex items-center gap-2">
                Next project <ArrowUpRight size={12} />
              </p>
              <p className="font-display text-[28px] md:text-[40px] leading-[1.05] tracking-[-0.02em] group-hover:opacity-80 transition-opacity">
                {next.title}
              </p>
              <p className="mt-2 italic font-serif text-[14px] text-[#bdbdb5]">
                {next.client}
              </p>
            </button>
          )}
        </div>

        <footer className="border-t border-[#2a2a2a]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[12px] text-[#8a8a85]">
            <span>© {new Date().getFullYear()} Evan Magee. All rights reserved.</span>
            <div className="flex items-center gap-6">
              <Link to="/" className="hover:text-[#f5f3ee] transition-colors duration-200">
                All work
              </Link>
              <Link to="/#contact" className="hover:text-[#f5f3ee] transition-colors duration-200">
                Contact
              </Link>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default ProjectDetail;
